import { create } from 'zustand';
import { QueryClient } from '@tanstack/react-query';
import { IniciarSesionType } from '@/app/autenticacion/types/InicarSesionType';
import { autenticacionService } from '@/app/autenticacion/api/autenticacionService';
import { localStorageService } from '@/shared/services/localStorageService';
import { cookieStorageService } from '@/shared/services/cookieStorageService';
import { autenticacionKeys } from '@/hooks/queries/useAutenticacion';
import { useLoaderStore } from '@/app/shared/components/common/spinner/loaderStore';

// QueryClient singleton para invalidar queries desde el store
let queryClient: QueryClient | null = null;
export const setQueryClient = (client: QueryClient) => {
  queryClient = client;
};

export type LoginResponse = { success: true; onboardingCompletado: boolean } | { success: false; message: string };

interface AuthState {
  user: IniciarSesionType | null;
  login: (values: IniciarSesionType) => Promise<LoginResponse>;
  recuperarContrasenia: (correo: string) => Promise<boolean>;
  setUser: (user: IniciarSesionType | null) => void;
  updateOnboardingCompletado: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  setUser: (user) => set({ user }),
  updateOnboardingCompletado: () => {
    const raw = localStorageService.getItem('current_user');
    if (raw) {
      const currentUser = typeof raw === 'string' ? JSON.parse(raw) : raw;
      localStorageService.setItem('current_user', JSON.stringify({ ...currentUser, onboardingCompletado: true }));
    }
  },
  login: async (values) => {
    const { startLoading, stopLoading } = useLoaderStore.getState();
    startLoading();
    try {
      cookieStorageService.removeItem('accessToken');
      cookieStorageService.removeItem('refreshToken');
      cookieStorageService.removeItem('sessionId');
      localStorageService.removeItem('current_user');

      const user = await autenticacionService.iniciarSesion(values);

      if (user.status == 200) {
        if (!user.data.success) {
          return {
            success: false,
            message: user.data.responseMessage || 'Credenciales incorrectas',
          };
        }

        cookieStorageService.setItem('accessToken', JSON.stringify(user.data.dataResponse.token));
        if (user.data.dataResponse.refreshToken) {
          cookieStorageService.setItem('refreshToken', JSON.stringify(user.data.dataResponse.refreshToken));
        }
        if (user.data.dataResponse.sessionId) {
          cookieStorageService.setItem('sessionId', user.data.dataResponse.sessionId);
        }
        localStorageService.setItem(
          'current_user',
          JSON.stringify({
            nombre: user.data.dataResponse.nombre,
            idUsuario: user.data.dataResponse.idUsuario,
            idTipoUsuario: user.data.dataResponse.idTipoUsuario,
            onboardingCompletado: user.data.dataResponse.onboardingCompletado ?? true,
            parametrosConfiguracion: {
              ...user.data.dataResponse.parametrosConfiguracion,
              googleMapsToken: user.data.dataResponse.parametrosConfiguracion.googleMapsToken,
            },
          })
        );

        if (values.recuerdame) {
          localStorageService.setItem('creds', JSON.stringify(values));
        } else {
          localStorageService.removeItem('creds');
        }

        // Invalidar query del menú para que TanStack Query lo recargue
        // El hook useMenuQuery sincronizará automáticamente con localStorage
        if (queryClient) {
          queryClient.invalidateQueries({ queryKey: autenticacionKeys.menu() });
        }

        set({ user: values });

        return { success: true, onboardingCompletado: user.data.dataResponse.onboardingCompletado ?? true };
      }

      return { success: false, message: 'Error de comunicación con el servidor' };
    } catch {
      return { success: false, message: 'No se pudo conectar con el servidor. Verifique su conexión.' };
    } finally {
      stopLoading();
    }
  },
  recuperarContrasenia: async (correo) => {
    const { startLoading, stopLoading } = useLoaderStore.getState();
    startLoading();
    try {
      const res = await autenticacionService.recuperarContrasenia(correo);
      return !!res.data.dataResponse;
    } finally {
      stopLoading();
    }
  },
}));
